import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getInstructions, createInstruction, updateInstruction, deleteInstruction, formatApiError } from "@/lib/api";

export function InstructionsManager({ token }) {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [newHi, setNewHi] = useState("");
  const [newEn, setNewEn] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { setItems(await getInstructions()); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    if (!newHi.trim() && !newEn.trim()) return toast.error("Enter an instruction");
    try {
      await createInstruction(token, newHi.trim(), newEn.trim());
      setNewHi(""); setNewEn("");
      toast.success("Instruction added");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async (it) => {
    const d = drafts[it.id] || {};
    const hi = d.hi !== undefined ? d.hi : it.hi;
    const en = d.en !== undefined ? d.en : it.en;
    try {
      await updateInstruction(token, it.id, hi, en);
      setDrafts((p) => { const n = { ...p }; delete n[it.id]; return n; });
      toast.success("Instruction updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (it) => {
    if (!window.confirm("Delete this instruction? This cannot be undone.")) return;
    try { await deleteInstruction(token, it.id); toast.success("Instruction deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setField = (id, field, value) => setDrafts((p) => ({ ...p, [id]: { ...(p[id] || {}), [field]: value } }));
  const val = (it, field) => (drafts[it.id] && drafts[it.id][field] !== undefined ? drafts[it.id][field] : (it[field] || ""));

  return (
    <div className="space-y-6" data-testid="instructions-manager">
      <div className="border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200/90">
        While this list is empty, customers see the built-in default instructions. Add your first entry to fully control what customers see on the Instructions page.
      </div>

      <div className="border border-white/10 bg-[#0A0A0A] p-4 space-y-3">
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">Add instruction</p>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-white/40 block mb-1">Hindi (हिंदी)</Label>
          <Input value={newHi} onChange={(e) => setNewHi(e.target.value)} data-testid="new-instruction-hi" placeholder="निर्देश हिंदी में…" className="rounded-none h-10 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-white/40 block mb-1">English</Label>
          <Input value={newEn} onChange={(e) => setNewEn(e.target.value)} data-testid="new-instruction-en" placeholder="Instruction in English…" className="rounded-none h-10 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
        </div>
        <Button onClick={add} data-testid="add-instruction-btn" className="rounded-none h-9 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-wider transition-colors">
          <Plus className="h-4 w-4 mr-1" /> Add Instruction
        </Button>
      </div>

      {loading && <p className="text-white/40 text-sm">Loading…</p>}
      {!loading && items.length === 0 && (
        <div className="border border-white/10 py-10 text-center text-white/40 text-sm">No custom instructions yet — customers see the defaults.</div>
      )}
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={it.id} className="border border-white/10 bg-[#0A0A0A] p-4 space-y-2" data-testid={`instruction-item-${it.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">#{i + 1}</span>
              <div className="flex gap-2">
                <Button onClick={() => save(it)} data-testid={`instruction-save-${it.id}`} variant="outline" className="rounded-none h-8 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase transition-colors">
                  <Save className="h-3.5 w-3.5 mr-1" /> Save
                </Button>
                <Button onClick={() => remove(it)} data-testid={`instruction-delete-${it.id}`} variant="outline" className="rounded-none h-8 border-red-500/40 bg-transparent text-red-400 hover:bg-red-500 hover:text-white text-xs uppercase transition-colors">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <Input value={val(it, "hi")} onChange={(e) => setField(it.id, "hi", e.target.value)} data-testid={`instruction-hi-${it.id}`} placeholder="Hindi" className="rounded-none h-10 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
            <Input value={val(it, "en")} onChange={(e) => setField(it.id, "en", e.target.value)} data-testid={`instruction-en-${it.id}`} placeholder="English" className="rounded-none h-10 bg-[#141414] border-white/10 text-white text-sm focus-visible:ring-2 focus-visible:ring-white/30" />
          </div>
        ))}
      </div>
    </div>
  );
}
